import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { modelsApi, modelProvidersApi } from '@/services/api';
import type { CreateModelInput, CreateModelProviderInput, ModelProvider, UpdateModelInput, UpdateModelProviderInput } from '@/types';

/**
 * The read-only model catalog (task 1074): Models and Model Providers. The
 * service gates both on `innovate.be.models.manage` (platform staff).
 */
export const useModels = (filters?: { providerId?: string; includeRetired?: boolean }) => {
  return useQuery({
    queryKey: ['models', filters],
    queryFn: () => modelsApi.list(filters).then(res => res.data.data),
  });
};

export const useModel = (id: string | undefined) => {
  return useQuery({
    queryKey: ['model', id],
    queryFn: () => modelsApi.get(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useModelProviders = () => {
  return useQuery({
    queryKey: ['model-providers'],
    queryFn: () => modelProvidersApi.list().then(res => res.data.data),
  });
};

// ---------------------------------------------------------------------------
// Catalog writes (task 1102). The service gates every one on
// `innovate.be.model-catalog.manage`; the page shows them under the mirrored
// `innovate.fe.model-catalog.manage` flag. Callers use `mutateAsync` so a
// refusal (400 field errors, 409 duplicate key or blocking Model Configs)
// reaches the dialog that asked.
// ---------------------------------------------------------------------------

export const useCreateModelProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateModelProviderInput) =>
      modelProvidersApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-providers'] });
    },
  });
};

export const useUpdateModelProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateModelProviderInput }) =>
      modelProvidersApi.update(id, data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-providers'] });
      // A disabled provider's Models leave the pickers; refresh the Models tab too.
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });
};

/**
 * Test connection. The result is written straight into the cached provider
 * row, so the Status cell changes in place before the list refetch lands.
 */
export const useVerifyModelProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => modelProvidersApi.verify(id).then(res => res.data.data),
    onSuccess: (result, id) => {
      queryClient.setQueryData<ModelProvider[]>(['model-providers'], (rows) =>
        rows?.map((row) =>
          row.id === id
            ? {
                ...row,
                verifiedAt: result.verified ? result.verifiedAt ?? null : null,
                lastVerifyError: result.verified ? null : result.lastVerifyError ?? null,
              }
            : row,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['model-providers'] });
    },
  });
};

/**
 * Remove a provider's stored platform key (task 1166). The result says whether
 * the provider fell back to its installed key; the list refetch repaints the
 * key cell from the service's own `keySource`.
 */
export const useRemoveModelProviderKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => modelProvidersApi.removeKey(id).then(res => res.data.data),
    onSuccess: (result, id) => {
      // The response carries the provider's new read, so the key cell repaints
      // before the list refetch lands.
      if (result.provider) {
        const updated = result.provider;
        queryClient.setQueryData<ModelProvider[]>(['model-providers'], (rows) =>
          rows?.map((row) => (row.id === id ? updated : row)),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['model-providers'] });
    },
  });
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateModelInput) => modelsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });
};

/**
 * Update one Model row (task 1093): the catalog review's "Use provider value",
 * "Mark deprecated / retired" and the Model page's edit form. Refreshes every
 * view that shows Model facts: the Models list, the Model page, the review
 * document, and Model Configs (each config read embeds its Model).
 */
export const useUpdateModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateModelInput }) =>
      modelsApi.update(id, data).then(res => res.data.data),
    onSuccess: (model) => {
      queryClient.setQueryData(['model', model.id], model);
      queryClient.invalidateQueries({ queryKey: ['models'] });
      queryClient.invalidateQueries({ queryKey: ['model', model.id] });
      queryClient.invalidateQueries({ queryKey: ['model-catalog-review'] });
      queryClient.invalidateQueries({ queryKey: ['model-configs'] });
    },
  });
};
